import { ReactNode } from 'react'
import Architecture from '../logic/Architecture'
import { Brick, LocalBrick, WebBrick } from '../logic/bricks'
import { Licenses } from '../logic/scenarios/data'

export type ArchitectureDiagramProps = {
    architecture: Architecture
    activeBrick?: Brick 
    onNavigate: (brick: Brick) => void
}

const legend = <aside className="legend"><ul>
    <li className="brick brick-local">Machine</li>    
    <li className="brick brick-container">Container</li>
    <li className="brick brick-web">Hosted by Gurobi</li>
</ul></aside>

const getLicenseName = (key: string|null): string => {
    if(key == null) {
        return ''
    }
    
    if(key in Licenses) {
        return Licenses[key].name
    }

    const clients: Record<string, string> = {
        'tsclient': 'Token Server client',
        'csclient': 'Compute Server client',
        'cmclient': 'Cluster Manager client'
    }
    return clients[key]
}

const createOrchestratorBrick = (items: JSX.Element[]): JSX.Element => (
    <li key='orchestrator' className='brick orchestrator'>Container orchestrator<ul className='diagram'>{[...items]}</ul></li>
)

const getBrickClass = (brick: Brick): string => {
    if(brick instanceof WebBrick) {
        return 'brick-web'
    }

    if(brick instanceof LocalBrick) {
        return 'brick-' + (brick.isContainer() ? 'container' : 'local')
    }

    return ''
}

const getIconClass = (brick: Brick): string => {
    if(brick.icon != null) {
        return 'icon-' + brick.icon
    }

    return ''
}

type DependencyProps = { type: string, children?: ReactNode }
const DependencyItem =  ({ type, children }: DependencyProps) => <li className={`dependency dependency-${type}`}>{children}</li>

export const ArchitectureDiagram = ({ architecture, onNavigate, activeBrick }: ArchitectureDiagramProps) => {
    const result: JSX.Element[] = []
    const orchestrated: JSX.Element[] = []
    
    const bricks = Array.from(architecture.bricks.values())
    bricks.sort((a,b) => b.sortIndex-a.sortIndex)

    bricks.forEach((brick, index) => {
        // We add the brick to either the temporary array of orchestrated container components, or the overall diagram
        const isOrchestrated =  (brick instanceof LocalBrick) && brick.isContainer() && architecture.containerOrchestration
        const targetArray = isOrchestrated ? orchestrated : result

        // If we want to write to the diagram and cached orchestrated container components before, write them out first
        if(!isOrchestrated && orchestrated.length > 0) {
            result.push(createOrchestratorBrick(orchestrated))
            orchestrated.length = 0
        }

        // Between bricks, add dependency arrows and figure out the direction first
        if(index > 0) {
            const dependencyTargetArray = (isOrchestrated && orchestrated.length > 0) ? orchestrated : result
            const isRightArrow = architecture.isDependency(bricks[index-1], brick)
            const isLeftArrow = architecture.isDependency(brick, bricks[index-1])

            if(isRightArrow) {
                const dependency = architecture.findDependency(bricks[index-1], brick)
                dependencyTargetArray.push(<DependencyItem key={`dependency-${index}`} type={dependency![2]}>{dependency![2]} &raquo;</DependencyItem>)
            } else if(isLeftArrow) {
                const dependency = architecture.findDependency(brick, bricks[index-1])
                dependencyTargetArray.push(<DependencyItem key={`dependency-${index}`} type={dependency![2]}>&laquo; {dependency![2]}</DependencyItem>)
            } 
        }
        const subtitle = getLicenseName(brick.license)
        const active = activeBrick == brick ? 'active' : ''
        targetArray.push(<li key={brick.key()} className={`brick ${getBrickClass(brick)} ${active}`} onClick={() => onNavigate(brick)}><div className={`icon ${getIconClass(brick)}`}></div> {brick.title} {subtitle && <div className='license'>{subtitle} license</div>}</li>)
    })

    if(orchestrated.length > 0) {
        result.push(createOrchestratorBrick(orchestrated))
    }
    
    return <div className="card text-bg-light">
    <h3 className="card-header">Architecture</h3>
    <div className="card-body architecture">
        <ul className="diagram">{result}</ul>
        {legend}
    </div></div>
}
export default ArchitectureDiagram